import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Badge } from '@/components';
import { Card } from '@/design-system/card';
import { Paginator } from '@/design-system/pagination';
import { SearchField } from '@/design-system/search-field';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import { Tabs, TabsList, TabsTrigger } from '@/design-system/tabs';
import { toast } from '@/design-system/toaster';
import {
  Provider,
  getListProvider,
  exportProviders,
} from '@/services/api/providers';
import { useUrlNumber, useUrlParam, useUrlSearch } from '@/hooks/urlState';
import AddProviderModal from './components/AddProviderModal';

type OnboardTab = 'assisted' | 'self-service';

const TABS: { key: OnboardTab; label: string }[] = [
  { key: 'assisted', label: 'Assisted' },
  { key: 'self-service', label: 'Self-Service' },
];

const TAB_KEYS = TABS.map((t) => t.key) as readonly OnboardTab[];

export const ALLOWED_BUSINESS_UNITS = [
  'X_DOCK',
  'SCHEDULED_INSTANT',
  '4_WHEEL',
] as const;

export const BUSINESS_UNIT_LABELS: Record<string, string> = {
  X_DOCK: 'X-Dock',
  SCHEDULED_INSTANT: 'Scheduled Instant',
  '4_WHEEL': '4 Wheel',
};

const REVENUE_STREAMS = [
  { value: '', label: 'All Business Units' },
  { value: 'X_DOCK', label: 'X-Dock' },
  { value: 'SCHEDULED_INSTANT', label: 'Scheduled Instant' },
  { value: '4_WHEEL', label: '4 Wheel' },
];

const STATUS_FILTERS = [
  { value: '', label: 'All Statuses' },
  { value: 'verified', label: 'Verified' },
  { value: 'pending', label: 'Pending' },
  { value: 'incomplete', label: 'Incomplete profile' },
];

const DEFAULT_PAGE_SIZE = 10;

export const ProvidersPage: React.FC = () => {
  const navigate = useNavigate();

  const [tab, setTab] = useUrlParam<OnboardTab>('tab', 'assisted', {
    allowed: TAB_KEYS,
    resetPage: true,
    history: 'push',
  });

  const {
    value: search,
    setValue: setSearch,
    committed: debouncedSearch,
  } = useUrlSearch('q', { resetPage: true, delay: 400 });

  const [revenueStream, setRevenueStream] = useUrlParam<string>(
    'revenue_stream',
    '',
    { resetPage: true }
  );

  const [statusFilter, setStatusFilter] = useUrlParam<string>('status', '', {
    resetPage: true,
  });

  const [page, setPage] = useUrlNumber('page', 1, { min: 1 });
  const [pageSize, setPageSize] = useUrlNumber('pageSize', DEFAULT_PAGE_SIZE, {
    min: 1,
    resetPage: true,
  });

  const [providers, setProviders] = useState<Provider[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const isSelfService = tab === 'self-service';

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const onboardCategory = isSelfService ? 'SELF_SERVICE' : 'ASSISTED';
      const effectiveRevenueStream =
        revenueStream || ALLOWED_BUSINESS_UNITS.join(',');
      const params: any = {
        page,
        size: pageSize,
        search: debouncedSearch?.trim() || undefined,
        revenue_stream: effectiveRevenueStream,
        onboard_category: onboardCategory,
      };

      if (isSelfService) {
        if (statusFilter === 'verified') {
          params.verified = true;
        } else if (statusFilter === 'pending') {
          params.verified = false;
          params.name_empty = false;
        } else if (statusFilter === 'incomplete') {
          params.verified = false;
          params.name_empty = true;
        }
      } else {
        params.verified = true;
      }

      const res = await getListProvider(params);
      const allowedSet = new Set<string>(
        revenueStream ? [revenueStream] : ALLOWED_BUSINESS_UNITS
      );
      const filtered = (res.data ?? []).filter(
        (p) => !p.revenue_stream || allowedSet.has(p.revenue_stream)
      );
      setProviders(filtered);
      setTotal(res.pagination?.total ?? filtered.length);
    } catch (err: any) {
      toast.error('Failed to load clients', {
        description: err.message,
      });
      setProviders([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, revenueStream, statusFilter, isSelfService]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const onboardCategory = isSelfService ? 'SELF_SERVICE' : 'ASSISTED';
      const effectiveRevenueStream =
        revenueStream || ALLOWED_BUSINESS_UNITS.join(',');
      const params: any = {
        search: debouncedSearch?.trim() || undefined,
        revenue_stream: effectiveRevenueStream,
        onboard_category: onboardCategory,
      };
      if (isSelfService) {
        if (statusFilter === 'verified') params.verified = true;
        else if (statusFilter === 'pending') {
          params.verified = false;
          params.name_empty = false;
        } else if (statusFilter === 'incomplete') {
          params.verified = false;
          params.name_empty = true;
        }
      } else {
        params.verified = true;
      }

      const blob = await exportProviders(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clients-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Clients exported successfully');
    } catch (err: any) {
      toast.error('Failed to export clients');
    } finally {
      setIsExporting(false);
    }
  };

  const handleRowClick = (record: Provider) => {
    navigate(`/providers/${record.id}`);
  };

  const inputCls =
    'rounded-gsm border border-stroke-soft-200 bg-white px-3 py-1.5 text-xs text-text-strong-950 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

  const columns: TableDataColumn<Provider>[] = [
    {
      key: 'index',
      title: 'No',
      width: '60px',
      render: (_v, _r, idx) => (
        <span className="font-mono text-xs tabular text-text-sub-600">
          {(page - 1) * pageSize + idx + 1}
        </span>
      ),
    },
    {
      key: 'code',
      title: 'Client Code',
      width: '120px',
      render: (_v, record) => (
        <span className="font-mono text-xs font-semibold text-text-strong-950 tabular">
          {record.code || '—'}
        </span>
      ),
    },
    {
      key: 'name',
      title: 'Client Name',
      render: (_v, record) => (
        <div>
          <span className="font-medium text-text-strong-950 tracking-tight">
            {record.name || '—'}
          </span>
          {record.legal_name && (
            <p className="text-xs text-text-sub-600 truncate max-w-sm">
              {record.legal_name}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'revenue_stream',
      title: 'Business Unit',
      width: '150px',
      render: (_v, record) => (
        <span className="text-xs text-text-sub-600 font-medium">
          {record.revenue_stream
            ? (BUSINESS_UNIT_LABELS[record.revenue_stream] || record.revenue_stream.replace(/_/g, ' '))
            : '—'}
        </span>
      ),
    },
    ...(!isSelfService
      ? [
          {
            key: 'created_at',
            title: 'Created Date',
            width: '150px',
            render: (_v: any, record: Provider) => {
              const val = record.created_at || record.createdAt;
              if (!val) return <span className="text-text-sub-600">—</span>;
              const date = new Date(val);
              return (
                <span className="font-mono text-xs tabular text-text-sub-600">
                  {date.toLocaleDateString('id-ID', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
              );
            },
          },
        ]
      : [
          {
            key: 'status',
            title: 'Status',
            width: '130px',
            render: (_v: any, record: Provider) => {
              if (record.verified_at) {
                return <Badge tone="success" dot>Verified</Badge>;
              }
              if (!record.name) {
                return <Badge tone="neutral" dot>Incomplete</Badge>;
              }
              return <Badge tone="warning" dot>Pending</Badge>;
            },
          },
          {
            key: 'self_order',
            title: 'Self-Order',
            width: '120px',
            render: (_v: any, record: Provider) => (
              <Badge
                tone={record.self_order_enabled ? 'success' : 'neutral'}
                dot
              >
                {record.self_order_enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            ),
          },
          {
            key: 'area',
            title: 'Area',
            render: (_v: any, record: Provider) => (
              <span className="text-xs text-text-sub-600 truncate max-w-xs block">
                {record.area || '—'}
              </span>
            ),
          },
        ]),
    {
      key: 'actions',
      title: '',
      width: '70px',
      align: 'right',
      render: (_v, record) => (
        <Button
          variant="ghost"
          size="xs"
          onClick={(e) => {
            e?.stopPropagation();
            navigate(`/providers/${record.id}`);
          }}
          aria-label="View details"
        >
          <i className="ri-arrow-right-line text-[16px] text-text-sub-600 hover:text-text-strong-950" />
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-5 p-6">
      {/* Page Header */}
      <header className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="label-eyebrow mb-2">
            <span className="text-primary font-bold mr-3">01</span>
            Master · Clients
          </p>
          <h1 className="text-[28px] font-extrabold tracking-tight text-text-strong-950 leading-none">
            Clients
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-text-sub-600 leading-relaxed">
            Manage enterprise clients, partner outlets, pricing schemes, and operational configurations.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="white"
            onClick={handleExport}
            disabled={isExporting}
          >
            <i className="ri-download-2-line mr-1.5" />
            {isExporting ? 'Downloading…' : 'Export CSV'}
          </Button>
          {!isSelfService && (
            <Button onClick={() => setAddModalOpen(true)}>
              <i className="ri-add-line mr-1.5" />
              Add Client
            </Button>
          )}
        </div>
      </header>

      {/* Main Content Card */}
      <Card padding="none">
        {/* Card Toolbar: Tabs + Filters */}
        <div className="flex flex-col gap-3 border-b border-stroke-soft-200 px-4 pt-1 pb-3 lg:flex-row lg:items-center lg:justify-between lg:pb-0">
          <Tabs value={tab} onValueChange={(v) => setTab(v as OnboardTab)}>
            <TabsList variant="line" className="w-auto border-0">
              {TABS.map((t) => (
                <TabsTrigger key={t.key} value={t.key}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="flex flex-wrap items-center justify-end gap-2.5 lg:py-2">
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Search code or name…"
              size="sm"
              className="w-56"
            />

            <select
              value={revenueStream}
              onChange={(e) => setRevenueStream(e.target.value)}
              className={inputCls}
            >
              {REVENUE_STREAMS.map((rs) => (
                <option key={rs.value} value={rs.value}>
                  {rs.label}
                </option>
              ))}
            </select>

            {isSelfService && (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={inputCls}
              >
                {STATUS_FILTERS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Data Table */}
        <div className="p-0">
          <TableData
            columns={columns}
            data={providers}
            loading={loading}
            onRowClick={handleRowClick}
            emptyState={
              <div className="p-12 text-center text-text-sub-600">
                <i className="ri-building-2-line text-3xl mb-2 text-text-sub-600/50" />
                <p className="text-sm font-medium">No clients found</p>
                <p className="text-xs mt-1 text-text-sub-600/70">
                  Try adjusting your search criteria or filters.
                </p>
              </div>
            }
          />

          {total > 0 && (
            <div className="p-4 border-t border-stroke-soft-200">
              <Paginator
                currentPage={page}
                totalItems={total}
                itemsPerPage={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </div>
          )}
        </div>
      </Card>

      {/* Add Client Modal */}
      <AddProviderModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => {
          fetchProviders();
        }}
      />
    </div>
  );
};

export default ProvidersPage;
